import {
  Component,
  OnDestroy,
  OnInit,
  Pipe,
  PipeTransform,
} from '@angular/core';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { UtilityFunctions } from 'src/app/helpers/utility-functions';
import { PriceLine } from 'src/app/model/price-line';
import Board from 'src/app/model/product/board/board';
import BoardPart, {
  BoardTypeChoices,
} from 'src/app/model/product/board/board-part';
import {
  ClientStickerPart,
  CLIENT_STICKER_PART_TYPE,
} from 'src/app/model/product/windowsticker/client-sticker-part';
import {
  ClientWindowSticker,
  CLIENT_WINDOW_STICKER_TYPE,
} from 'src/app/model/product/windowsticker/client-windowsticker';
import StandardStickerPart from 'src/app/model/product/windowsticker/standard-sticker-part';
import { ClientBaseProjectPriceSummaryComponent } from 'src/app/pages/projects/client-corner/create-view-base-project/client-base-project-summary/client-base-project-price-summary/client-base-project-price-summary.component';
import { ClientCompanySelectService } from 'src/app/services/client-company-select.service';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { SignPlacementPriceCalculationRequestService } from 'src/app/services/sign-placement-price/sign-placement-price-calculation-request.service';

@Component({
  selector: 'facturation-card',
  templateUrl: './facturation-card.component.html',
  styleUrls: ['./facturation-card.component.scss'],
})
export class FacturationCardComponent
  extends ClientBaseProjectPriceSummaryComponent
  implements OnInit, OnDestroy
{
  BOARD_TYPE_CHOICES = BoardTypeChoices;
  otherPriceLines: PriceLine[] = [];

  constructor(
    protected projectDisplayObservable: ProjectDisplayObservableService,
    protected signPlacementPriceCalculationRequestService: SignPlacementPriceCalculationRequestService,
    protected clientCompanySelectService: ClientCompanySelectService
  ) {
    super(
      projectDisplayObservable,
      signPlacementPriceCalculationRequestService,
      clientCompanySelectService
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    const displayPricesObservable = this.createObservableUntilDestroy(
      this.projectDisplayObservable.getProductSizeChangedNoSideEffectSubscribable(),
      this.projectDisplayObservable.getPaintedPostsChangedSubscribable()
    );
    this.addSubscription(displayPricesObservable, () => {
      this.calculateDisplayPrices();
    });

    this.createdDate =
      this.baseProject.createdAt != null
        ? this.baseProject.createdAt
        : this.project.createdAt != null
        ? this.project.createdAt
        : new Date();

    if (
      this.baseProject != null &&
      CollectionHelper.isPopulated(this.baseProject.projectInvoices)
    ) {
      this.otherPriceLines = this.baseProject.projectInvoices
        .filter((invoice) => invoice.type === 'PlacementInvoice')
        .flatMap((invoice) =>
          invoice.priceLines.filter((priceLine) => priceLine.override == null)
        );
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  signSizeFilter(boardPart: BoardPart): boolean {
    return boardPart.signSize != null;
  }

  productSizeFilter(part: StandardStickerPart): boolean {
    return part.productSize != null;
  }

  reduceClientWindowStickerPlacementPrice(part: ClientStickerPart) {
    if (
      part != null &&
      part.partType === CLIENT_STICKER_PART_TYPE.STANDARD_STICKER_PART &&
      part.instance != null &&
      (part.instance as StandardStickerPart).productSize != null
    ) {
      return (part.instance as StandardStickerPart).productSize.placementPrice;
    } else if (
      part != null &&
      part.partType === CLIENT_STICKER_PART_TYPE.STICKER_PART
    ) {
      return part['placementPrice'];
    } else {
      return null;
    }
  }

  reduceClientWindowStickerProductionPrice(part: ClientStickerPart) {
    if (
      part != null &&
      part.partType === CLIENT_STICKER_PART_TYPE.STANDARD_STICKER_PART &&
      part.instance != null &&
      (part.instance as StandardStickerPart).productSize != null
    ) {
      return (part.instance as StandardStickerPart).productSize.productionPrice;
    } else if (
      part != null &&
      part.partType === CLIENT_STICKER_PART_TYPE.STICKER_PART
    ) {
      return part['productionPrice'];
    } else {
      return null;
    }
  }

  reduceStandardWindowStickerPlacementPrice(part: StandardStickerPart) {
    if (part != null && part.productSize != null) {
      return part.productSize.placementPrice;
    } else {
      return null;
    }
  }

  reduceStandardWindowStickerProductionPrice(part: StandardStickerPart) {
    if (part != null && part.productSize != null) {
      return part.productSize.productionPrice;
    } else {
      return null;
    }
  }

  reduceBoardPlacementPrice(part: BoardPart) {
    if (part != null && part.signSize != null) {
      return part.signSize.placementPrice;
    } else {
      return null;
    }
  }

  reduceBoardPaintedPostsPrice(part: BoardPart) {
    if (part != null && part.signSize != null && part.paintedPosts != null) {
      return part.signSize.paintedPostsPrice;
    } else {
      return null;
    }
  }

  reduceBoardProductionPrice(part: BoardPart) {
    if (part != null && part.signSize != null) {
      return part.signSize.productionPrice;
    } else {
      return null;
    }
  }

  private calculateDisplayPrices(): void {
    if (!CollectionHelper.isPopulated(this.project.products)) {
      return;
    }
    this.project.products.forEach((p) => {
      switch (p.type) {
        case CLIENT_WINDOW_STICKER_TYPE:
          this.calculateClientWindowStickerPriceDisplay(<ClientWindowSticker>p);
          break;
        case this.PRODUCT_TYPES.BOARD.value:
          this.calculateBoardPriceDisplay(<Board>p);
          break;
      }
    });
  }

  private calculateClientWindowStickerPriceDisplay(
    windowSticker: ClientWindowSticker
  ): void {
    if (!CollectionHelper.isPopulated(windowSticker.clientStickerParts)) {
      windowSticker.calculatedPriceDisplay = null;
      return;
    }
    const totalProductionPrice = windowSticker.clientStickerParts
      .map((sp) => {
        if (
          sp.partType === CLIENT_STICKER_PART_TYPE.STANDARD_STICKER_PART &&
          sp.instance != null
        ) {
          const standardStickerPart = <StandardStickerPart>sp.instance;
          return standardStickerPart.productSize == null
            ? null
            : standardStickerPart.productSize.productionPrice;
        }
        return null;
      })
      .reduce(UtilityFunctions.nullGuardSum);
    const totalPlacementPrice = windowSticker.clientStickerParts
      .map((sp) => {
        if (
          sp.partType === CLIENT_STICKER_PART_TYPE.STANDARD_STICKER_PART &&
          sp.instance != null
        ) {
          const standardStickerPart = <StandardStickerPart>sp.instance;
          return standardStickerPart.productSize == null
            ? null
            : standardStickerPart.productSize.placementPrice;
        }
        return null;
      })
      .reduce(UtilityFunctions.nullGuardSum);
    windowSticker.calculatedPriceDisplay = UtilityFunctions.nullGuardSum(
      totalProductionPrice,
      totalPlacementPrice
    );
  }

  private calculateBoardPriceDisplay(board: Board): void {
    if (!CollectionHelper.isPopulated(board.boardParts)) {
      board.calculatedAggregatePrice = null;
      board.calculatedPaintedPostsPrice = null;
    }
    let company = null;
    if (this.client && this.baseProject.id != null) {
      company = this.clientCompanySelectService.getSelectedCompany();
    } else if (this.project.projectContacts.length > 0) {
      company = this.project.projectContacts[0].company;
    }
    const calculatedProductionPrice = board.boardParts
      .map((bp) => {
        if (bp.signSize != null) {
          return company != null &&
            company.isRetailer &&
            bp.signSize.productionRetailPrice != null
            ? bp.signSize.productionRetailPrice
            : bp.signSize.productionPrice;
        }
        return null;
      })
      .reduce(UtilityFunctions.nullGuardSum);
    const calculatedPlacementPrice = board.boardParts
      .map((bp) => {
        if (bp.signSize != null) {
          return company != null &&
            company.isRetailer &&
            bp.signSize.placementRetailPrice != null
            ? bp.signSize.placementRetailPrice
            : bp.signSize.placementPrice;
        }
        return null;
      })
      .reduce(UtilityFunctions.nullGuardSum);
    board.calculatedAggregatePrice = UtilityFunctions.nullGuardSum(
      calculatedProductionPrice,
      calculatedPlacementPrice
    );
    board.calculatedPaintedPostsPrice = board.boardParts
      .map((bp) => {
        if (bp.signSize != null && bp.paintedPosts) {
          return bp.signSize.paintedPostsPrice;
        }
        return null;
      })
      .reduce(UtilityFunctions.nullGuardSum);
  }
}

@Pipe({
  name: 'reduce',
})
export class ReducePipe implements PipeTransform {
  transform(value: any[], predicate?: (e: any) => boolean) {
    return predicate == null || value == null
      ? value
      : value.reduce(predicate, 0);
  }
}

@Pipe({
  name: 'nullCheck',
})
export class NullCheckPipe implements PipeTransform {
  transform(value: any, predicate?: (e: any) => boolean): boolean {
    return value != null && value != '0' && value != 0;
  }
}
